import type { UIEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import { PostDeploymentInstructionsView } from "../../components/PostDeploymentInstructionsView";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { PAGE_SIZE } from "../../constants/pdm";
import type { DeploymentTemplate, DsApp } from "../../types/pdm.types";
import {
  getDsApps,
  getTemplates,
  getParameterTemplates,
  getCommitmentLevels,
  createDeployment,
  type CommitmentLevel,
} from "../../services/pdmApi.service";
import { getAvailableNetworks } from "../../services/deploymentsManagement.service";
import {
  buildDeploymentParametersPayload,
  parseComplexParamsFromSchema,
  validateSchemaFields,
} from "../../utils/deploymentParameters";
import { routePaths } from "../../routePaths";
import { repositoryUrlIsWebLink } from "../../utils/repositoryRemote";
import type { AvailableNetwork, JsonSchemaProperty } from "../../types/pdm.types";
import { CCTPDomainSelector } from "./CCTPDomainSelector";
import { BridgeNetworkSelector } from "./BridgeNetworkSelector";
import { DSTokenAddressChecker } from "./DSTokenAddressChecker";

const baseSteps = ["Select DSApp", "Configuration", "Parameters"] as const;

const NETWORK_SELECT_LABEL = "Select a network";

const TEMPLATES_LIST_MAX_HEIGHT_PX = 320;

function dynamicFieldKeys(template: DeploymentTemplate | null) {
  const keys = Object.keys(template?.initializationSchema?.properties ?? {});
  return keys.filter((k) => k !== "network");
}

function isComplexField(prop: JsonSchemaProperty | undefined) {
  return prop?.type === "object" || prop?.type === "array";
}

function helperTextForField(prop: JsonSchemaProperty | undefined) {
  const jsonHelperTextSuffix = "The content must be a valid JSON format";
  if (isComplexField(prop)) {
    return prop?.description
      ? `${prop.description} — ${jsonHelperTextSuffix}`
      : jsonHelperTextSuffix;
  }
  return prop?.description;
}

function getFieldActionConfig(args: {
  schemaTitle: string | undefined;
  fieldKey: string;
  availableNetworks: AvailableNetwork[];
  selectedNetwork: string;
}) {
  const { schemaTitle, fieldKey, availableNetworks, selectedNetwork } = args;

  if (
    schemaTitle === "DeployUSDCBridgeProtocolParams" &&
    fieldKey === "cctp-domains"
  ) {
    const selectableNetworks = availableNetworks.filter(
      (item) => item.id !== selectedNetwork,
    );

    return {
      label: "Add CCTP Domains",
      disabled: selectableNetworks.length === 0,
      dialogType: "cctp" as const,
    };
  }

  if (schemaTitle === "DeployBridgeProtocolParams" && fieldKey === "bridges") {
    return {
      label: "Add Bridges",
      disabled: false,
      dialogType: "bridges" as const,
    };
  }

  return null;
}

export default function CreateDeploymentView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedDsApp, setSelectedDsApp] = useState<DsApp | null>(null);
  const [dsAppsPage, setDsAppsPage] = useState(0);
  const [dsAppNameFilter, setDsAppNameFilter] = useState("");
  const [dsAppFetchErrorOpen, setDsAppFetchErrorOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [network, setNetwork] = useState("");
  const [selectedTemplate, setSelectedTemplate] =
    useState<DeploymentTemplate | null>(null);
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [meta, setMeta] = useState({
    name: "",
    branch: "dev",
    description: "",
    ownerAddress: "",
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [deploymentCreatedDialogOpen, setDeploymentCreatedDialogOpen] =
    useState(false);
  const [createdDeploymentId, setCreatedDeploymentId] = useState<string | null>(
    null,
  );
  const [cctpDialogOpen, setCctpDialogOpen] = useState(false);
  const [bridgeDialogOpen, setBridgeDialogOpen] = useState(false);
  const [dsIssuerId, setDsIssuerId] = useState("");

  const hasPostInstructions = Boolean(
    selectedTemplate?.postDeploymentInstructions?.length,
  );

  const steps = useMemo(() => {
    if (!hasPostInstructions) {
      return [...baseSteps];
    }
    return [...baseSteps, "Post Deployment Instructions"];
  }, [hasPostInstructions]);

  useEffect(() => {
    setActiveStep((s) => Math.min(s, steps.length - 1));
  }, [steps.length]);

  const dsAppsQuery = useQuery({
    queryKey: ["dsApps", "create-deployment", dsAppsPage],
    queryFn: async () => {
      try {
        return await getDsApps({
          offset: dsAppsPage * PAGE_SIZE,
          limit: PAGE_SIZE,
        });
      } catch (error) {
        console.error("Failed to fetch DSApps: ", error);
        setDsAppFetchErrorOpen(true);
        throw error;
      }
    },
  });

  const filteredDsAppItems = useMemo(() => {
    const items = dsAppsQuery.data?.items ?? [];
    const q = dsAppNameFilter.trim().toLowerCase();
    if (!q) {
      return items;
    }
    return items.filter((p) => p.name.toLowerCase().includes(q));
  }, [dsAppsQuery.data?.items, dsAppNameFilter]);

  const templatesInfiniteQuery = useInfiniteQuery({
    queryKey: [
      "templates",
      "create-deployment",
      selectedDsApp?.id ?? "none",
    ],
    queryFn: ({ pageParam }) =>
      getTemplates(selectedDsApp!.id, {
        offset: pageParam,
        limit: PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const nextOffset = lastPageParam + lastPage.items.length;
      if (lastPage.items.length < PAGE_SIZE) {
        return undefined;
      }
      if (nextOffset >= lastPage.total) {
        return undefined;
      }
      return nextOffset;
    },
    enabled: Boolean(selectedDsApp?.id),
  });

  const {
    data: templatesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: templatesPending,
    isError: isTemplatesQueryError,
    error: templatesQueryError,
  } = templatesInfiniteQuery;

  const allTemplates = useMemo(
    () => templatesData?.pages.flatMap((p) => p.items) ?? [],
    [templatesData?.pages],
  );

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    if (!q) {
      return allTemplates;
    }
    return allTemplates.filter((t) => {
      const fields = [t.name, t.contractName ?? "", t.deploymentTask];
      return fields.some((f) => f.toLowerCase().includes(q));
    });
  }, [allTemplates, templateSearch]);

  const handleTemplatesScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 64;
      if (nearBottom && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  const networksQuery = useQuery({
    queryKey: ["available-networks", "create-deployment"],
    queryFn: getAvailableNetworks,
    enabled: Boolean(selectedTemplate),
  });

  const commitmentLevelsQuery = useQuery<CommitmentLevel[]>({
    queryKey: ["commitment-levels", network],
    queryFn: () => getCommitmentLevels(network),
    enabled: Boolean(network),
    staleTime: 5 * 60 * 1000,
  });

  const availableNetworks = useMemo(
    () => networksQuery.data ?? [],
    [networksQuery.data],
  );

  const fieldKeys = useMemo(
    () => dynamicFieldKeys(selectedTemplate),
    [selectedTemplate],
  );

  const mandatoryParamKeys = useMemo(() => {
    const req = selectedTemplate?.initializationSchema?.required ?? [];
    return req.filter((k) => k !== "network");
  }, [selectedTemplate]);

  const optionalParamKeys = useMemo(
    () => fieldKeys.filter((k) => !mandatoryParamKeys.includes(k)),
    [fieldKeys, mandatoryParamKeys],
  );

  const parameterTemplatesQuery = useQuery({
    queryKey: ["parameter-templates", selectedTemplate?.id, network],
    queryFn: () =>
      getParameterTemplates({
        deploymentTemplateId: selectedTemplate!.id,
        network,
        offset: 0,
        limit: 10,
      }),
    enabled:
      activeStep === 2 && Boolean(selectedTemplate?.id && network),
  });

  const firstParameterTemplate = parameterTemplatesQuery.data?.items?.[0];

  useEffect(() => {
    const pt = parameterTemplatesQuery.data?.items?.[0];
    if (!pt || !selectedTemplate) {
      return;
    }
    const required = (
      selectedTemplate.initializationSchema.required ?? []
    ).filter((k) => k !== "network");
    setParamValues((prev) => {
      const next = { ...prev };
      for (const key of required) {
        const preset = pt.presetParameters[key];
        const addr = preset?.address?.trim();
        if (addr) {
          next[key] = addr;
        }
      }
      return next;
    });
  }, [
    firstParameterTemplate?.id,
    firstParameterTemplate?.updatedAt,
    selectedTemplate,
    network,
    parameterTemplatesQuery.data?.items,
  ]);

  const createMutation = useMutation({
    mutationFn: createDeployment,
    onSuccess: (deployment) => {
      void queryClient.invalidateQueries({ queryKey: ["deployments"] });
      void queryClient.invalidateQueries({ queryKey: ["deploymentStats"] });
      setCreatedDeploymentId(deployment.id);
      setDeploymentCreatedDialogOpen(true);
    },
  });

  const handleNext = () => {
    setFormError(null);
    if (activeStep === 0 && !selectedDsApp) {
      setFormError("Select a DSApp to continue.");
      return;
    }
    if (activeStep === 1) {
      if (!selectedTemplate) {
        setFormError("Select a deployment template.");
        return;
      }
      if (!network) {
        setFormError("Select a network.");
        return;
      }
    }
    setActiveStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const handleBack = () => {
    setFormError(null);
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const handleOpenCctpDialog = useCallback(() => {
    setCctpDialogOpen(true);
  }, []);

  const handleCloseCctpDialog = useCallback(() => {
    setCctpDialogOpen(false);
  }, []);

  const handleAcceptCctpDomains = useCallback((value: string) => {
    setParamValues((prev) => ({
      ...prev,
      "cctp-domains": value,
    }));
    setCctpDialogOpen(false);
  }, []);

  const handleOpenBridgeDialog = useCallback(() => {
    setBridgeDialogOpen(true);
  }, []);

  const handleCloseBridgeDialog = useCallback(() => {
    setBridgeDialogOpen(false);
  }, []);

  const handleAcceptBridges = useCallback((value: string) => {
    setParamValues((prev) => ({
      ...prev,
      bridges: value,
    }));
    setBridgeDialogOpen(false);
  }, []);

  const renderParameterField = useCallback(
    (key: string, required: boolean, readOnly = false) => {
      const prop = selectedTemplate?.initializationSchema.properties?.[key];
      const helperText = helperTextForField(prop);
      const actionConfig = getFieldActionConfig({
        schemaTitle: selectedTemplate?.initializationSchema.title,
        fieldKey: key,
        availableNetworks,
        selectedNetwork: network,
      });

      const isDSTokenField =
        selectedTemplate?.initializationSchema.title ===
          "DeployBridgeProtocolParams" && key === "token";

      const isConsistencyLevelField =
        selectedTemplate?.initializationSchema.title ===
          "DeployBridgeProtocolParams" && key === "consistency-level";

      return (
        <Stack
          key={key}
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "flex-start" }}
          sx={{ mb: 2, "&:last-of-type": { mb: 0 } }}
        >
          {isConsistencyLevelField ? (
            <Box sx={{ width: "100%" }}>
              <FormControl
                fullWidth
                size="small"
                disabled={
                  !network ||
                  commitmentLevelsQuery.isLoading ||
                  readOnly
                }
              >
                <InputLabel id={`${key}-label`}>
                  {prop?.title ?? key}
                </InputLabel>
                <Select
                  labelId={`${key}-label`}
                  label={prop?.title ?? key}
                  value={paramValues[key] ?? ""}
                  onChange={(e) =>
                    setParamValues((prev) => ({
                      ...prev,
                      [key]: e.target.value,
                    }))
                  }
                  endAdornment={
                    commitmentLevelsQuery.isLoading ? (
                      <CircularProgress size={16} sx={{ mr: 3 }} />
                    ) : undefined
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {(commitmentLevelsQuery.data ?? []).map((lvl) => (
                    <MenuItem key={lvl.value} value={lvl.value}>
                      {lvl.level} — {lvl.value}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {commitmentLevelsQuery.isError && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.5, px: 1.75 }}
                >
                  Failed to load levels —{" "}
                  {commitmentLevelsQuery.error?.message}
                </Typography>
              )}
              {!commitmentLevelsQuery.isError && helperText && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5, px: 1.75 }}
                >
                  {helperText}
                </Typography>
              )}
              {!network && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5, px: 1.75 }}
                >
                  Select a network to load commitment levels.
                </Typography>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5, px: 1.75 }}
              >
                Consistency levels are defined by Wormhole. See{" "}
                <Link
                  href="https://wormhole.com/docs/products/reference/consistency-levels/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  consistency levels reference
                </Link>{" "}
                for details.
              </Typography>
            </Box>
          ) : isDSTokenField ? (
            <DSTokenAddressChecker
              label={prop?.title ?? key}
              value={paramValues[key] ?? ""}
              onChange={(value) =>
                setParamValues((prev) => ({
                  ...prev,
                  [key]: value,
                }))
              }
              onIssuerChange={setDsIssuerId}
              required={required}
              fullWidth
              size="small"
              helperText={helperText}
              placeholder={
                prop?.examples?.length ? prop.examples[0] : undefined
              }
              disabled={readOnly}
              network={network}
            />
          ) : (
            <TextField
              label={prop?.title ?? key}
              value={paramValues[key] ?? ""}
              onChange={(e) =>
                setParamValues((prev) => ({
                  ...prev,
                  [key]: e.target.value,
                }))
              }
              required={required}
              fullWidth
              size="small"
              helperText={helperText}
              placeholder={
                prop?.examples?.length ? prop.examples[0] : undefined
              }
              multiline={isComplexField(prop)}
              minRows={isComplexField(prop) ? 4 : undefined}
              InputProps={readOnly ? { readOnly: true } : undefined}
            />
          )}
          {actionConfig && (
            <Button
              variant="outlined"
              onClick={
                actionConfig.dialogType === "cctp"
                  ? handleOpenCctpDialog
                  : handleOpenBridgeDialog
              }
              disabled={actionConfig.disabled}
              sx={{
                minWidth: { xs: "100%", md: 180 },
                alignSelf: { xs: "stretch", md: "flex-start" },
                mt: { xs: 0, md: 0.5 },
              }}
            >
              {actionConfig.label}
            </Button>
          )}
        </Stack>
      );
    },
    [
      availableNetworks,
      commitmentLevelsQuery,
      handleOpenBridgeDialog,
      handleOpenCctpDialog,
      network,
      paramValues,
      selectedTemplate,
    ],
  );

  const handleSubmit = () => {
    setFormError(null);
    if (!selectedDsApp || !selectedTemplate || !network) {
      setFormError("Incomplete selection.");
      return;
    }
    if (!meta.name.trim() || !meta.branch.trim()) {
      setFormError("Name and branch are required.");
      return;
    }
    if (!meta.ownerAddress.trim()) {
      setFormError("Owner address is required.");
      return;
    }
    const schemaErrors = validateSchemaFields(
      selectedTemplate.initializationSchema,
      paramValues,
    );
    if (schemaErrors.length) {
      setFormError(schemaErrors.join(" "));
      return;
    }
    const { requiredParameters, optionalParameters } =
      buildDeploymentParametersPayload(
        paramValues,
        mandatoryParamKeys,
        optionalParamKeys,
        firstParameterTemplate?.presetParameters,
      );

    const requiredTransformed = parseComplexParamsFromSchema({
      schema: selectedTemplate.initializationSchema,
      rawParams: requiredParameters,
      omitEmpty: false,
      forceParse: {
        bridges: "array",
        "cctp-domains": "array",
      },
    });
    const optionalTransformed = parseComplexParamsFromSchema({
      schema: selectedTemplate.initializationSchema,
      rawParams: optionalParameters,
      omitEmpty: true,
      forceParse: {
        bridges: "array",
        "cctp-domains": "array",
      },
    });
    const jsonErrors = [
      ...requiredTransformed.errors,
      ...optionalTransformed.errors,
    ];
    if (jsonErrors.length) {
      setFormError(jsonErrors.join(" "));
      return;
    }

    createMutation.mutate({
      name: meta.name.trim(),
      deploymentTemplateId: selectedTemplate.id,
      network,
      branch: meta.branch.trim(),
      description: meta.description.trim(),
      ownerAddress: meta.ownerAddress.trim(),
      requiredParameters: requiredTransformed.params,
      optionalParameters: optionalTransformed.params,
    });
  };

  const handleDeploymentCreatedDialogClose = () => {
    setDeploymentCreatedDialogOpen(false);
    const id = createdDeploymentId;
    setCreatedDeploymentId(null);
    if (id) {
      navigate(routePaths.deploymentLogs(id));
    }
  };

  const dsAppsTotal = dsAppsQuery.data?.total ?? 0;
  const nextDisabled =
    (activeStep === 0 && !selectedDsApp) ||
    (activeStep === 1 &&
      (!selectedTemplate || !network.trim()));

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: "brand.main" }}>
        Create deployment
      </Typography>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Dialog
        open={dsAppFetchErrorOpen}
        onClose={() => setDsAppFetchErrorOpen(false)}
        aria-labelledby="ds-app-fetch-error-title"
      >
        <DialogTitle id="ds-app-fetch-error-title">Error</DialogTitle>
        <DialogContent>
          <DialogContentText>
            DSApp list could not be loaded. Please try again later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDsAppFetchErrorOpen(false)}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deploymentCreatedDialogOpen}
        onClose={handleDeploymentCreatedDialogClose}
        aria-labelledby="deployment-created-title"
      >
        <DialogTitle id="deployment-created-title">
          Deployment created
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your deployment was created successfully. It is currently in{" "}
            <strong>pending</strong> status. When the process picks it up for
            execution, the status will change to <strong>in progress</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeploymentCreatedDialogClose}
            variant="contained"
            autoFocus
          >
            View logs
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cctpDialogOpen}
        onClose={handleCloseCctpDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select CCTP domains</DialogTitle>
        <CCTPDomainSelector
          availableNetworks={availableNetworks}
          selectedNetwork={network}
          value={paramValues["cctp-domains"] ?? ""}
          onAccept={handleAcceptCctpDomains}
          onCancel={handleCloseCctpDialog}
        />
      </Dialog>

      <Dialog
        open={bridgeDialogOpen}
        onClose={handleCloseBridgeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select bridges</DialogTitle>
        <BridgeNetworkSelector
          availableNetworks={availableNetworks}
          selectedNetwork={network}
          value={paramValues.bridges ?? ""}
          issuerId={dsIssuerId}
          onAccept={handleAcceptBridges}
          onCancel={handleCloseBridgeDialog}
        />
      </Dialog>

      {formError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formError}
        </Alert>
      )}
      {createMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {createMutation.error instanceof Error
            ? createMutation.error.message
            : "Request failed"}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        {activeStep === 0 && (
          <Box>
            <Typography sx={{ mb: 1 }}>Available DSApps</Typography>
            <TextField
              label="Filter by name"
              size="small"
              value={dsAppNameFilter}
              onChange={(e) => {
                setDsAppNameFilter(e.target.value);
                setDsAppsPage(0);
              }}
              fullWidth
              sx={{ mb: 2, maxWidth: 400 }}
            />
            {dsAppsQuery.isPending ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                <CircularProgress size={22} />
                <Typography color="text.secondary">Loading DSApps…</Typography>
              </Box>
            ) : (
              <>
                <List dense disablePadding id="DSApps-list">
                  {filteredDsAppItems.map((p) => (
                    <ListItemButton
                      key={p.id}
                      selected={selectedDsApp?.id === p.id}
                      onClick={() => {
                        setSelectedDsApp(p);
                        setSelectedTemplate(null);
                        setNetwork("");
                        setTemplateSearch("");
                        setParamValues({});
                      }}
                    >
                      <ListItemText
                        primary={p.name}
                        secondaryTypographyProps={{ component: "div" }}
                        secondary={
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Version: {p.version}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Main Branch: {p.gitBranch}
                            </Typography>
                            {repositoryUrlIsWebLink(p.repositoryUrl) ? (
                              <Link
                                href={p.repositoryUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                sx={{ wordBreak: "break-all" }}
                              >
                                {p.repositoryUrl}
                              </Link>
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                              >
                                {p.repositoryUrl}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  ))}
                  {!filteredDsAppItems.length &&
                    !(dsAppsQuery.data?.items ?? []).length && (
                      <Typography color="text.secondary">
                        No DSApps returned from the API.
                      </Typography>
                    )}
                  {!filteredDsAppItems.length &&
                    Boolean((dsAppsQuery.data?.items ?? []).length) && (
                      <Typography color="text.secondary">
                        No DSApps match this filter on this page.
                      </Typography>
                    )}
                </List>
                <TablePagination
                  component="div"
                  count={dsAppsTotal}
                  page={dsAppsPage}
                  onPageChange={(_, newPage) => setDsAppsPage(newPage)}
                  rowsPerPage={PAGE_SIZE}
                  rowsPerPageOptions={[PAGE_SIZE]}
                  onRowsPerPageChange={() => undefined}
                />
              </>
            )}
            {selectedDsApp && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {selectedDsApp.description}
              </Alert>
            )}
          </Box>
        )}

        {activeStep === 1 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: 560,
            }}
          >
            <Typography variant="subtitle2">Deployment template</Typography>
            {templatesPending && !templatesData ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                <CircularProgress size={22} />
                <Typography color="text.secondary">
                  Loading templates…
                </Typography>
              </Box>
            ) : isTemplatesQueryError ? (
              <Alert severity="error">
                {templatesQueryError instanceof Error
                  ? templatesQueryError.message
                  : "Failed to load deployment templates."}
              </Alert>
            ) : (
              <>
                <TextField
                  placeholder="Search templates..."
                  size="small"
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  fullWidth
                />
                <Box
                  onScroll={handleTemplatesScroll}
                  sx={{
                    maxHeight: TEMPLATES_LIST_MAX_HEIGHT_PX,
                    overflowY: "auto",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                  }}
                >
                  <List dense disablePadding id="deployment-templates-list">
                    {filteredTemplates.map((t) => (
                      <ListItemButton
                        key={t.id}
                        selected={selectedTemplate?.id === t.id}
                        onClick={() => {
                          setSelectedTemplate(t);
                          setNetwork("");
                          setParamValues({});
                        }}
                      >
                        <ListItemText
                          primary={t.name}
                          secondaryTypographyProps={{ component: "div" }}
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {`Contract: ${t.contractName ?? "—"}`}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ wordBreak: "break-word" }}
                              >
                                {t.deploymentTask}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    ))}
                    {!allTemplates.length && (
                      <Typography color="text.secondary" sx={{ p: 2 }}>
                        No templates for this DSApp.
                      </Typography>
                    )}
                    {Boolean(allTemplates.length) &&
                      !filteredTemplates.length && (
                        <Typography color="text.secondary" sx={{ p: 2 }}>
                          No templates match your search.
                        </Typography>
                      )}
                  </List>
                  {isFetchingNextPage && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 1.5,
                      }}
                    >
                      <CircularProgress size={22} />
                    </Box>
                  )}
                </Box>
              </>
            )}
            {selectedTemplate && (
              <Typography variant="body2" color="text.secondary">
                {selectedTemplate.description}
              </Typography>
            )}

            <FormControl
              fullWidth
              size="small"
              disabled={
                !selectedTemplate ||
                networksQuery.isPending ||
                networksQuery.isError
              }
            >
              <InputLabel id="network-label">{NETWORK_SELECT_LABEL}</InputLabel>
              <Select
                id="network-select"
                labelId="network-label"
                label={NETWORK_SELECT_LABEL}
                displayEmpty
                value={network}
                onChange={(e) => {
                  setNetwork(e.target.value);
                  setParamValues({});
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return "";
                  }
                  return (
                    availableNetworks.find((n) => n.id === selected)?.name ??
                    selected
                  );
                }}
              >
                {availableNetworks.map((n) => (
                  <MenuItem key={n.id} value={n.id}>
                    {n.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {Boolean(selectedTemplate) && networksQuery.isPending && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading networks…
                </Typography>
              </Box>
            )}
            {Boolean(selectedTemplate) && networksQuery.isError && (
              <Alert severity="error">
                {networksQuery.error instanceof Error
                  ? networksQuery.error.message
                  : "Failed to load available networks."}
              </Alert>
            )}
          </Box>
        )}

        {activeStep === 2 && selectedTemplate && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxWidth: 560,
            }}
          >
            <TextField
              label="Deployment name"
              value={meta.name}
              onChange={(e) =>
                setMeta((m) => ({ ...m, name: e.target.value }))
              }
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Branch"
              value={meta.branch}
              onChange={(e) =>
                setMeta((m) => ({ ...m, branch: e.target.value }))
              }
              required
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              value={meta.description}
              onChange={(e) =>
                setMeta((m) => ({ ...m, description: e.target.value }))
              }
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            <TextField
              label="Owner address"
              value={meta.ownerAddress}
              onChange={(e) =>
                setMeta((m) => ({ ...m, ownerAddress: e.target.value }))
              }
              required
              fullWidth
              size="small"
            />

            <Divider sx={{ my: 1 }} />

            <Typography variant="subtitle2" color="text.secondary">
              Deployment parameters
            </Typography>

            {parameterTemplatesQuery.isPending && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}
              >
                <CircularProgress size={22} />
                <Typography color="text.secondary">
                  Loading parameter templates…
                </Typography>
              </Box>
            )}

            {parameterTemplatesQuery.isError && (
              <Alert severity="error">
                {parameterTemplatesQuery.error instanceof Error
                  ? parameterTemplatesQuery.error.message
                  : "Failed to load parameter templates."}
              </Alert>
            )}

            {!parameterTemplatesQuery.isPending &&
              parameterTemplatesQuery.isSuccess &&
              firstParameterTemplate && (
                <Alert severity="info" sx={{ typography: "body2" }}>
                  {firstParameterTemplate.description}
                </Alert>
              )}

            {!parameterTemplatesQuery.isPending &&
              parameterTemplatesQuery.isSuccess &&
              !firstParameterTemplate && (
                <Alert severity="warning">
                  No parameter templates were returned for this template and
                  network. Enter all values manually below.
                </Alert>
              )}

            {!parameterTemplatesQuery.isPending && (
              <>
                <Card variant="outlined">
                  <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Mandatory parameters
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {mandatoryParamKeys.length === 0 ? (
                      <Typography color="text.secondary" variant="body2">
                        None
                      </Typography>
                    ) : (
                      mandatoryParamKeys.map((key) => {
                        const hasPresetAddress = Boolean(
                          firstParameterTemplate?.presetParameters[
                            key
                          ]?.address?.trim(),
                        );
                        const readOnly =
                          parameterTemplatesQuery.isSuccess &&
                          Boolean(firstParameterTemplate) &&
                          hasPresetAddress;
                        return renderParameterField(key, true, readOnly);
                      })
                    )}
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Optional parameters
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {optionalParamKeys.length === 0 ? (
                      <Typography color="text.secondary" variant="body2">
                        None
                      </Typography>
                    ) : (
                      optionalParamKeys.map((key) =>
                        renderParameterField(key, false),
                      )
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </Box>
        )}

        {activeStep === 3 && selectedTemplate && hasPostInstructions && (
          <Box sx={{ maxWidth: 720 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Post deployment instructions
            </Typography>
            <PostDeploymentInstructionsView
              instructions={selectedTemplate.postDeploymentInstructions ?? []}
              context={{ network, provider: network }}
            />
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={nextDisabled}
            >
              Next
            </Button>
          )}
          {activeStep === steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? "Submitting…" : "Create deployment"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
